import { EToastType, useToast } from '@bfe/moly';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import {
  post__v3_private_cht_asset_file_core_export_config,
  post__v3_private_cht_asset_file_core_export_create_account_statement,
  post__v3_private_cht_asset_file_core_export_list,
  post__v3_private_cht_asset_file_core_export_url,
} from '@/services/assetFileCore';

import {
  EXPORT_CREATE_TYPE,
  EXPORT_TYPE,
  STATEMENT_DATE_TYPE,
} from './constants';

const useStatement = () => {
  const [$t, lang] = useI18nContext();
  const { toast } = useToast();
  const DATE_FORMAT = 'YYYYMMDD';
  const DEFAULT_COUNT = 50; // 可导出最大次数
  const PAGE_SIZE = 20;
  const { msgError, user } = useUserInfo();
  // 账户结单时间类型
  const [statementDateType, setStatementDateType] = useState<string>(
    STATEMENT_DATE_TYPE.MONTHLY_STATEMENT,
  );
  // 月结单月份
  const [monthlyValues, setMonthlyValues] = useState<string | string[]>([]);
  // 自定义日期
  const [customizedTime, setCustomizedTime] = useState<any>(null);

  // table loading
  const [isLoading, setIsLoading] = useState(false);
  // 列表原始数据
  const [resultOriginData, setRresultOriginData] = useState([]);
  // 列表当前页展示数据
  const [resultData, setResultData] = useState<any>([]);
  // 正在下载的id列表
  const [downloadIds, setDownloadIds] = useState<any>([]);
  // 是否有生成中状态
  const [isUpdating, setIsUpdating] = useState(false);
  // 当前页码
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [curExportCount, setCurExportCount] = useState(DEFAULT_COUNT); // 当日剩余次数 默认5
  const [maxExportCount, setMaxExportCount] = useState(DEFAULT_COUNT); // 总次数默认5

  // 月份列表
  const monthlyList = useMemo(() => {
    // 获取当前日期
    const today = dayjs();

    // 获取今天的年份和月份
    const currentYear = today.year();
    const currentMonth = today.month(); // 0-11

    // 计算一年前的年份和月份
    const startDate = today.subtract(1, 'year');
    const YEAR_START = startDate.year();
    const MONTH_START = startDate.month() + 1; // 因为要在下面的条件中使用，所以加1以便于比较

    // 存储结果的数组
    const months = [];
    // 从当前月份的前一个月开始，向前遍历到 2022 年 1 月
    let year = currentYear;
    let month = currentMonth - 1; // 从当前月份的前一个月开始
    while (
      year > YEAR_START ||
      (year === YEAR_START && month >= MONTH_START - 1)
    ) {
      // 如果月份小于0，调整为上一年
      if (month < 0) {
        month = 11; // 12月
        year -= 1;
      }

      // 推入数组
      months.push({
        value: dayjs
          ?.utc(`${year}-${month + 1}-01`)
          ?.endOf('month')
          ?.format('YYYY-MM-DD'), // 生成utc时间戳
        label: `${year}/${month + 1}`, // 月份从0开始，+1
      });

      // 递减月份
      month -= 1;
    }
    return months;
  }, []);

  // 自定义日期选择范围
  const disabledDate = useCallback((current: Dayjs) => {
    // 获取当前日期
    const today = dayjs();
    // 选择日期时间不能早于1年前
    const tooEarly = today.diff(current, 'years') >= 1;
    // 选择日期时间不能晚于昨天
    const tooLate = current.diff(today, 'days') >= 0;
    return !!tooEarly || !!tooLate;
  }, []);

  // 导出按钮的状态
  const exportBtnStatus = useMemo(() => {
    // 如果是月结单
    if (statementDateType === STATEMENT_DATE_TYPE.MONTHLY_STATEMENT) {
      // 没有选择月份列表，则置灰
      if (monthlyValues?.length === 0) {
        return true;
      }

      // 如果选的月份数量大于剩余次数，则置灰
      if (monthlyValues?.length > curExportCount) {
        return true;
      }
    }

    // 自定义时间，并且没有选择时间，则置灰
    if (
      (statementDateType === STATEMENT_DATE_TYPE.CUSTOMIZED_TIME &&
        !customizedTime) ||
      curExportCount <= 0
    ) {
      return true;
    }

    return false;
  }, [monthlyValues, customizedTime, statementDateType, curExportCount]);

  // 账户结单时间类型切换
  const handlerDateTypeChange = (vals: string) => {
    setStatementDateType(vals);
  };

  // 月结单月份切换
  const handlerMonthlyChange = (vals: string | string[]) => {
    setMonthlyValues(vals as string[]);
  };

  // 自定义日期切换
  const handlerCustomizedTimeChange = (value: any, dateString: string) => {
    setCustomizedTime(value);
  };

  // 获取当页数据
  const handlerGetCurrentPageData = (data: any, number: number) => {
    const currentPageData = [...data].splice(
      (number - 1) * PAGE_SIZE,
      PAGE_SIZE,
    );
    setResultData(currentPageData);
  };

  // 本地翻页逻辑切换
  const handlerPaginationChange = (current: any, pageSize: any) => {
    setCurrentPage(current);
    handlerGetCurrentPageData(resultOriginData, current);
  };

  // 获取 table 数据
  const getDownloadList = async () => {
    setIsLoading(true);
    try {
      const res = await post__v3_private_cht_asset_file_core_export_list({
        type: EXPORT_TYPE.ACCOUNT_STATEMENT,
      });
      const data = res?.result?.records ?? [];
      setCurrentPage(1);
      setRresultOriginData(data);
      handlerGetCurrentPageData(data, 1);
    } catch (error) {
      setRresultOriginData([]);
      setResultData([]);
    }
    setIsLoading(false);
  };

  // 获取导出配置 只获取可导出数量
  const getExportConfig = async () => {
    try {
      const res = await post__v3_private_cht_asset_file_core_export_config({
        type: EXPORT_TYPE.ACCOUNT_STATEMENT,
      });
      setCurExportCount(res?.result?.count);
    } catch (error) {
      /* empty */
    }
  };

  // 从列表中剔除当前id
  const sliceDownloadIds = (id: any) => {
    setDownloadIds((prev: any) => {
      const newPrev = [...prev];
      return newPrev.filter((item) => item !== id);
    });
  };

  // 下载导出文件
  const handleDownload = async (row: any) => {
    const id = row?.id;
    const endTime = row.getValue('endTime');
    // 如果正在下载，不再触发下载逻辑
    if (downloadIds.includes(id)) {
      return;
    }
    try {
      // 下载开始前先把id放入正在下载id列表中
      setDownloadIds((prev: any) => [...prev, id] as any);
      const res = await post__v3_private_cht_asset_file_core_export_url({
        id,
      });
      const shortTimeUrl = res?.result?.shortTimeUrl;
      const sign = res?.result?.sign;
      // 如果没有下载地址和签名则不做处理
      if (shortTimeUrl && sign) {
        // 开始下载文件
        const response = await axios.get(shortTimeUrl, {
          headers: {
            'content-sign': sign,
          },
          responseType: 'blob',
        });

        // 将响应内容转换为二进制数据
        const { data } = response;
        // 创建一个二进制数据对象并使用URL.createObjectURL()方法创建URL
        const blob = new Blob([data], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);

        const endDate = dayjs(Number(endTime) * 1000).format(DATE_FORMAT);
        // todo 此处后面需要确认是用zip文件。还是pdf。
        // 导出文件名
        const filename =
          user?.id > 0
            ? `Bybit_Account_Statement_${user?.id}_${endDate}.zip`
            : `Bybit_Account_Statement_${endTime}.zip`;
        // 让用户选择文件保存的位置和文件名，并保存文件
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
      }

      sliceDownloadIds(id);
    } catch (error) {
      // todo 看看是否需要数据上报
      sliceDownloadIds(id);
    }
  };

  // 生成报表
  const handleGenerateClick = async () => {
    let endTimes: any = monthlyValues;
    if (statementDateType === STATEMENT_DATE_TYPE.CUSTOMIZED_TIME) {
      endTimes = [dayjs(customizedTime).format('YYYY-MM-DD')];
    }

    const params = {
      end_times: endTimes,
      language: lang,
    };

    try {
      setIsUpdating(true);
      const res =
        await post__v3_private_cht_asset_file_core_export_create_account_statement(
          params,
        );
      const result = res?.result;
      if (result?.code === EXPORT_CREATE_TYPE.FAIL) {
        toast({
          iconType: EToastType.FAIL,
          title: result?.msg,
        });
      } else {
        toast({
          iconType: EToastType.SUCCESS,
          title: $t('exportErrorSuccess01'),
        });
        setMonthlyValues([]);
        setCustomizedTime(null);
        getExportConfig();
        getDownloadList();
      }
      // todo 如果后续需要一条一条记录导出，则需要注释掉
      setIsUpdating(false);
    } catch (error: any) {
      const ret_code = error?.ret_code;
      const ret_msg = error?.ret_msg;
      if (ret_code) {
        const tmsKey = `exportErrorMsg${ret_code}`;
        const tmsValue = $t(tmsKey);
        if (tmsKey === tmsValue) {
          toast({
            iconType: EToastType.FAIL,
            title: `${ret_code}:${ret_msg}`,
          });
        } else {
          toast({
            iconType: EToastType.FAIL,
            title: tmsValue,
          });
        }
      }
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    setCurExportCount(DEFAULT_COUNT);
    setMaxExportCount(DEFAULT_COUNT);
    getExportConfig();
    getDownloadList();
  }, []);

  return {
    isLoading,
    isUpdating,
    exportBtnStatus,
    resultOriginData,
    resultData,
    statementDateType,
    handlerDateTypeChange,
    monthlyList,
    monthlyValues,
    handlerMonthlyChange,
    disabledDate,
    customizedTime,
    handlerCustomizedTimeChange,
    handlerPaginationChange,
    downloadIds,
    handleDownload,
    curExportCount,
    maxExportCount,
    handleGenerateClick,
    currentPage,
    PAGE_SIZE,
  };
};

export default useStatement;
